import { useEffect, useState } from 'react';

import { ApiClient, ApiError } from '../api/client';
import type { InstallationType, PriceRow } from '../api/types';
import { formatToman, toJalaliDate } from '../format';

/* THE one place prices are set.
 *
 * Six numbers: for each of the three operations, what the customer pays and what
 * the installer keeps. The third column — what the installer owes — is computed
 * as you type and again on the server. It is shown but never typed, because a
 * number a human enters is a number that can disagree with the other two, and the
 * first sign of that is an installer arguing about their balance.
 *
 * Saving inserts a new set of rows rather than editing the old ones. Installations
 * already recorded keep the amounts they were billed at.
 */

interface Props {
  api: ApiClient;
  onAuthLost: () => void;
}

const LABELS: Record<InstallationType, string> = {
  INITIAL: 'نصب اولیه (به همراه یک سال اشتراک)',
  RENEWAL: 'تمدید اشتراک یک‌ساله',
  REINSTALL: 'نصب مجدد (بدون تمدید اشتراک)',
};

const ORDER: InstallationType[] = ['INITIAL', 'RENEWAL', 'REINSTALL'];

interface Draft {
  customerPrice: string;
  installerShare: string;
}

export function PricingScreen({ api, onAuthLost }: Props) {
  const [rows, setRows] = useState<PriceRow[] | null>(null);
  const [history, setHistory] = useState<PriceRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const guard = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      if (e instanceof ApiError && e.isUnauthenticated) return onAuthLost();
      setError(e instanceof Error ? e.message : 'خطای ناشناخته');
    }
  };

  const load = () =>
    guard(async () => {
      const current = await api.pricing();
      setRows(current);
      const next: Record<string, Draft> = {};
      for (const row of current) {
        next[row.type] = {
          customerPrice: row.customerPrice,
          installerShare: row.installerShare,
        };
      }
      setDraft(next);
    });

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (type: InstallationType, field: keyof Draft, value: string) => {
    /* Digits only. Everything is whole tomans; a stray separator or a Persian
     * digit would reach the server as an invalid amount. */
    const digits = value.replace(/[^\d۰-۹]/g, '').replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
    setDraft((d) => ({ ...d, [type]: { ...d[type], [field]: digits } }));
    setSaved(false);
  };

  const owes = (type: InstallationType): string => {
    const d = draft[type];
    if (!d) return '0';
    try {
      /* BigInt, not Number: these are the same tomans the server keeps in BigInt,
       * and a value beyond 2^53 must still subtract correctly. */
      const value = BigInt(d.customerPrice || '0') - BigInt(d.installerShare || '0');
      return value.toString();
    } catch {
      return '0';
    }
  };

  const invalid = ORDER.some((type) => {
    const d = draft[type];
    if (!d || d.customerPrice === '' || d.installerShare === '') return true;
    try {
      return BigInt(d.installerShare) > BigInt(d.customerPrice);
    } catch {
      return true;
    }
  });

  const changed = rows?.some(
    (row) =>
      draft[row.type] &&
      (draft[row.type].customerPrice !== row.customerPrice ||
        draft[row.type].installerShare !== row.installerShare),
  );

  const save = () =>
    guard(async () => {
      setBusy(true);
      try {
        const updated = await api.setPricing(
          ORDER.map((type) => ({
            type,
            customerPrice: draft[type].customerPrice,
            installerShare: draft[type].installerShare,
          })),
          note.trim() || undefined,
        );
        setRows(updated);
        setNote('');
        setSaved(true);
        setHistory(null);
      } finally {
        setBusy(false);
      }
    });

  const loadHistory = () => guard(async () => setHistory(await api.pricingHistory()));

  if (rows === null && !error) return <div className="card muted">در حال بارگذاری…</div>;

  return (
    <div>
      {error && <div className="alert error">{error}</div>}
      {saved && <div className="alert info">قیمت‌های جدید ثبت شد.</div>}

      <div className="card">
        <div className="muted" style={{ marginBottom: 16 }}>
          برای هر عملیات دو عدد وارد کنید: مبلغی که مالک خودرو می‌پردازد، و سهمی که
          نزد نصاب می‌ماند. بدهی نصاب خودش حساب می‌شود.
        </div>

        {ORDER.map((type) => (
          <div key={type} style={{ marginBottom: 22 }}>
            <div style={{ marginBottom: 8 }}>{LABELS[type]}</div>
            <div className="row" style={{ alignItems: 'flex-end' }}>
              <label style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
                <span>قیمت برای مشتری</span>
                <input
                  value={draft[type]?.customerPrice ?? ''}
                  onChange={(e) => set(type, 'customerPrice', e.target.value)}
                  dir="ltr"
                  inputMode="numeric"
                />
              </label>
              <label style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
                <span>سهم نصاب</span>
                <input
                  value={draft[type]?.installerShare ?? ''}
                  onChange={(e) => set(type, 'installerShare', e.target.value)}
                  dir="ltr"
                  inputMode="numeric"
                />
              </label>
              {/* Read-only on purpose: typed, it becomes a third number that can
                  disagree with the other two. */}
              <div style={{ flex: 1, minWidth: 160 }}>
                <span className="muted" style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
                  بدهی نصاب به شما
                </span>
                <div style={{ padding: '10px 14px', fontSize: 15 }}>{formatToman(owes(type))}</div>
              </div>
            </div>
          </div>
        ))}

        <label>
          <span>یادداشت این تغییر (اختیاری)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="مثلاً: افزایش نرخ ۱۴۰۵" />
        </label>

        <div className="row">
          <button className="primary" disabled={busy || invalid || !changed} onClick={() => void save()}>
            {busy ? 'در حال ثبت…' : 'ثبت قیمت‌های جدید'}
          </button>
          {invalid && <span className="muted">سهم نصاب نمی‌تواند از قیمت مشتری بیشتر باشد.</span>}
          {!invalid && !changed && <span className="muted">تغییری داده نشده است.</span>}
        </div>
      </div>

      <div className="card">
        <div className="spread">
          <strong>تاریخچه قیمت‌ها</strong>
          <button onClick={() => (history ? setHistory(null) : void loadHistory())}>
            {history ? 'بستن' : 'نمایش'}
          </button>
        </div>
        {history && (
          <table style={{ marginTop: 14 }}>
            <thead>
              <tr>
                <th>عملیات</th>
                <th>مشتری</th>
                <th>سهم نصاب</th>
                <th>بدهی نصاب</th>
                <th>از تاریخ</th>
                <th>یادداشت</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row, i) => (
                <tr key={i}>
                  <td>{LABELS[row.type]}</td>
                  <td>{formatToman(row.customerPrice)}</td>
                  <td>{formatToman(row.installerShare)}</td>
                  <td>{formatToman(row.installerOwes)}</td>
                  <td>{toJalaliDate(row.activeFrom)}</td>
                  <td className="muted">{row.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {history && history.length === 0 && <div className="muted">چیزی ثبت نشده است.</div>}
        {!history && (
          <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
            هر تغییر قیمت یک ردیف تازه می‌سازد و ردیف قبلی دست‌نخورده می‌ماند. نصب‌هایی
            که قبلاً ثبت شده‌اند با همان مبلغ زمان خودشان باقی می‌مانند.
          </div>
        )}
      </div>

      {rows && (
        <div className="card muted" style={{ fontSize: 13 }}>
          قیمت‌های فعلی از {toJalaliDate(rows[0]?.activeFrom)} اعمال شده‌اند.
        </div>
      )}
    </div>
  );
}
